import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ChannelType } from 'app/shared/constants/channel-type.constants';
import { EMPTY, Observable, Subject, Subscription, timer } from 'rxjs';
import { catchError, delayWhen, filter, finalize, map, retryWhen, switchMap } from 'rxjs/operators';

import { TwoFADevice } from '../../../pages/security/twostepverification/model/device.model';
import { AuthorizationStatus } from '../../constants/authorization-status-constants';
import { OperationType } from '../../constants/operation-type.constants';
import {
  AuthorizationEndRequest,
  AuthorizationEndResponse,
  AuthorizationStartRequest,
  AuthorizationStartResponse,
  AuthorizationStatusResponse,
} from '../../model/authorization.model';
import { ContactRequest, ContactResponse } from '../../model/contacts.model';
import { AuthorizationService } from '../../service/authorization.service';
import { AbstractModalWithFormComponent } from '../abstract-modal-with-form.component';
import { WizardModalResult, WizardModalResultType } from '../model/wizard-modal-result.model';
import { VerificationCodeFormComponent } from './verification-code-form.component';
import { AppConfiguration } from 'app/core/models/app-configuration.model';
import { TwoStepVerificationSmsService } from 'app/pages/security/twostepverification/extraUser/two-step-verification-sms-service';
import { ContactType } from 'app/shared/constants/contact-type.constants';
import { Timeouts } from 'app/shared/constants/timeout.constants';

/**
 * Enumeration used inside the TwoFactorAppFlowComponent for state management
 */
export enum TwoFactorFlowStatus {
  PENDING = 'pending',
  EXPIRED = 'expired',
  GEN_CODE_OTP = 'gencodeOTP',
  GEN_CODE_SMS = 'gencodeSMS',
  FAILURE = 'failure',
  SUCCESS = 'success',
}

/**
 * Two Factor App Flow Component
 * @description Angular utility Component. Contain the entire Two Factor Application flow template and logic
 */
@Component({
  selector: 'ap-two-factor-app-flow',
  templateUrl: 'two-factor-app-flow.component.html',
})
export class TwoFactorAppFlowComponent extends AbstractModalWithFormComponent implements OnInit, OnDestroy {
  // tslint:disable-next-line:no-input-rename
  @Input('translateKey') inputTranslateKey: string;
  // tslint:disable-next-line:no-input-rename
  @Input('errorTranslateKey') inputErrorTranslateKey: string;
  @Input() mobileContact?: ContactResponse;
  @Input() opType?: OperationType;
  @Input() authReq?: AuthorizationStartResponse;
  @Input() withConfirmExtraMessage = false;
  @Input() isMultiUserWithSmsTwoFa = false;
  @Input() paireOtp = false;
  @Input() typeTitle = "";
  @Input() noDocaPosteView = false;

  @Input() successFunction?: (a: AuthorizationEndResponse) => Observable<unknown>;

  eventsFocus: Subject<boolean> = new Subject<boolean>();

  successStep = false;

  currentDevice$: Observable<TwoFADevice | undefined> = EMPTY;
  currentAuthRequest: AuthorizationStartResponse | null;
  status: TwoFactorFlowStatus = TwoFactorFlowStatus.PENDING;
  statusList = TwoFactorFlowStatus;
  pollingSubscription?: Subscription;
  CHANNELS = ChannelType;
  _videoStringUrl = '';
  _imgStringUrl = '';
  imageViewSafary = false;
  videoViewSafary = true;
  extraErrorMessage = '';

  @ViewChild('verifyFormComponent')
  public verifyFormComponent?: VerificationCodeFormComponent;

  constructor(
    private readonly authService: AuthorizationService,
    public twoSvSmsSrv: TwoStepVerificationSmsService,
    public activeModal: NgbActiveModal,
    protected translateService: TranslateService,
    private readonly config: AppConfiguration
  ) {
    super(translateService, 'twoFa', 'twoFa');
    this.currentAuthRequest = null;
    this.inputTranslateKey = this.inputErrorTranslateKey = 'twoFa';
    this._videoStringUrl = window.origin + '/content/images/dispositivo.mp4';
    this._imgStringUrl = window.origin + '/content/images/dispositivo.jpg';
  }

  /**
   * Component initialization.
   */
  ngOnInit(): void {
    this.updateTranslateKey(this.inputTranslateKey);
    this.updateErrorTranslateKey(this.inputErrorTranslateKey);

    if (this.successFunction) {
      this.successStep = true;
    }

    if (this.isSafaryWebView()) {
      this.imageViewSafary = true;
      this.videoViewSafary = false;
    } else {
      this.imageViewSafary = false;
      this.videoViewSafary = true;
    }

    this.currentAuthRequest = this.authReq ?? null;
    this.swithcTwoFactorFlow(this.currentAuthRequest?.channel as ChannelType);
    if (!this.isMultiUserWithSmsTwoFa && this.currentAuthRequest?.channel === ChannelType.PUSH_NOTIFICATION) {
      setTimeout(() => this.startPolling(), Timeouts.TIMEOUT_5000);
    }
  }

  isSafaryWebView(): boolean {
    let result = false;
    const userAgent = window.navigator.userAgent.toLowerCase();
    const safari = userAgent.includes('safari');
    const ios = /iphone|ipod|ipad/.test(userAgent);

    if (ios) {
      if (safari) {
        // browser
        result = false; // view video
      } else {
        // webview
        result = true; // view image
      }
    } else {
      // not iOS
      if (userAgent.includes(this.config.appVersionAndroid)) {
        result = true; // view image
      }
    }

    return result;
  }

  /**
   * Start authorization flow. In case of Push notification channel type, start polling to server
   * otherwise inizialize another flow and show specific step to user
   * @param authRequest Initial Authorization
   */
  swithcTwoFactorFlow(channel: ChannelType): void {
    switch (channel) {
      case ChannelType.PUSH_NOTIFICATION:
        this.status = TwoFactorFlowStatus.PENDING;
        break;
      case ChannelType.DEVICE_OTP:
        this.status = TwoFactorFlowStatus.GEN_CODE_OTP;
        this.eventsFocus.next(true);
        break;
      default:
        this.status = TwoFactorFlowStatus.GEN_CODE_SMS;
        this.eventsFocus.next(true);
    }
  }

  /**
   * Start polling and wait user push notification confirm
   */
  startPolling(): void {
    this.pollingSubscription = this.authService
      .getAuthorizationStatus(this.currentAuthRequest?.authRequestId ?? '')
      .pipe(
        map((res: AuthorizationStatusResponse) => {
          if (res.status === AuthorizationStatus.PENDING) {
            throw new HttpErrorResponse({ status: 1000, error: { pairStatus: res.status, nextRequest: res.nextRequestNotBefore } });
          } else {
            this.pollingCheckResult(res);
          }
        }),
        retryWhen(errors => {
          return errors.pipe(
            delayWhen(() => {
              const nextRequestTime = 5000;
              return timer(nextRequestTime);
            }),
            filter(error => {
              if (error.status === 0 || error.status.toString().startsWith('5')) {
                this.status = TwoFactorFlowStatus.FAILURE;
                return false;
              }
              return true;
            })
          );
        })
      )
      .subscribe();
  }

  /**
   * Method for check push notification result
   * @param authStartResponse  Authorization Status response
   */
  pollingCheckResult(authStartResponse: AuthorizationStatusResponse): void {
    this.unbindSubscription();

    switch (authStartResponse.status) {
      case AuthorizationStatus.FAILED:
        // case AuthorizationStatus.DONE:
        // caso di fallimento, mostrare pagina di fallimento
        this.status = TwoFactorFlowStatus.FAILURE;
        break;
      case AuthorizationStatus.EXPIRED:
        this.status = TwoFactorFlowStatus.EXPIRED;
        break;
      case AuthorizationStatus.DONE:
        this.status = TwoFactorFlowStatus.SUCCESS;
        break;
      default:
        this.startEndAuthRequest();
    }
  }

  /**
   * Method that start authorization again for channel after user has selected channel type retry method
   * @param selectedChannel Channel used for start Authorization
   */
  selectedRetryMethod(selectedChannel: ChannelType): void {
    this.swithcTwoFactorFlow(selectedChannel);
    this.authService.startAuthorization(new AuthorizationStartRequest(this.opType as OperationType, selectedChannel)).subscribe(
      (res: AuthorizationStartResponse) => {
        this.eventsFocus.next(true);
        this.currentAuthRequest = res;
        if (this.currentAuthRequest.channel === ChannelType.PUSH_NOTIFICATION) {
          setTimeout(() => this.startPolling(), Timeouts.TIMEOUT_5000);
        }
      },
      (resError: HttpErrorResponse) => {
        if (selectedChannel === ChannelType.PUSH_NOTIFICATION) {
          this.status = TwoFactorFlowStatus.FAILURE;
          if (resError.status !== 0 && !resError.status.toString().startsWith('5')) {
            this.extraErrorMessage = this.translateService.instant(
              'accountSecurity.twoFactorAuth.errors.' + resError.error.errorKey,
              resError.error.errorValue
            );
          }
        } else {
          this.showErrorOnControl(
            this.verifyFormComponent?.codeForm.get('code') ?? null,
            resError.status,
            resError.error.errorKey,
            resError.error.errorValue
          );
        }
      }
    );
  }

  /**
   * Method that complete the current flow and invoke success function if defined
   * else close the opened dialog
   * @param verificationCode (optional) verification code obtained from authorization method
   */
  startEndAuthRequest(verificationCode?: string): void {
    if (this.isMultiUserWithSmsTwoFa) {
      this.deactivateTwoFactor(verificationCode);
      return;
    }
    const canShowSpinner = this.getCanShowSpinner();

    if (canShowSpinner) {
      this.verifyFormComponent?.showSpinner();
    }


    this.authService
      .endAuthorization(this.currentAuthRequest?.authRequestId ?? '', this.getVerificationRequest(verificationCode))
      .pipe(
        catchError((resError: HttpErrorResponse) => {
          this.handleEndAuthorizationError(resError);
          return EMPTY;
        })
      )
      .subscribe((authEndResponse: AuthorizationEndResponse) => {
        this.handleEndAuthorizationSuccess(authEndResponse);
      });
  }

  private getCanShowSpinner() {
    return this.status === TwoFactorFlowStatus.GEN_CODE_OTP || this.status === TwoFactorFlowStatus.GEN_CODE_SMS;
  }

  private getVerificationRequest(verificationCode?: string) {
    if (!verificationCode) {
      // caso di end per notifica push, devo solo mettere consume a true
      return new AuthorizationEndRequest('', true, undefined, undefined, undefined);
    }
    // il consume impostato a true per operation delete
    const consumed = this.status !== TwoFactorFlowStatus.GEN_CODE_SMS
    return new AuthorizationEndRequest(verificationCode, consumed, false, this.opType, this.currentAuthRequest?.channel);
  }

  private handleEndAuthorizationError(resError: HttpErrorResponse) {
    if (this.getCanShowSpinner()) {
      this.verifyFormComponent?.hideSpinner();
      this.showErrorOnControl(this.verifyFormComponent?.codeForm.get('code') ?? null, resError.status, resError.error.errorKey);
    } else {
      this.status = TwoFactorFlowStatus.FAILURE;
    }
  }

  private handleEndAuthorizationSuccess(authEndResponse: AuthorizationEndResponse) {
    if (authEndResponse.success) {
      this.handleAuthEndResponseSuccess(authEndResponse);
      return;
    }
    this.handleAuthEndResponseFailure(authEndResponse)
  }

  private handleAuthEndResponseSuccess(authEndResponse: AuthorizationEndResponse) {
    const canShowSpinner = this.getCanShowSpinner();
    if (this.successStep) {
      this.callSuccessFunction(authEndResponse, canShowSpinner);
      return;
    }
    if (canShowSpinner) {
      this.verifyFormComponent?.hideSpinner();
    }
    this.closeTwoFactorAppFlow(authEndResponse);
  }

  private handleAuthEndResponseFailure(authEndResponse: AuthorizationEndResponse) {
    const canShowSpinner = this.getCanShowSpinner();
    if (canShowSpinner) {
      this.verifyFormComponent?.hideSpinner();
      this.showErrorOnControl(
        this.verifyFormComponent?.codeForm.get('code') ?? null,
        HttpStatusCode.Ok,
        authEndResponse.remainingAttempts > 0 ? 'authorization.codeError' : 'authorization.maximumRetryCount'
      );
    } else {
      this.status = TwoFactorFlowStatus.FAILURE;
    }
  }

  /**
   * Complete procedure and switch 2fa
   * @param code verification code obtained from authorization service
   */
  deactivateTwoFactor(code?: string): void {
    this.verifyFormComponent?.showSpinner();

    const authorizationRequest = new AuthorizationEndRequest(code, false, false, OperationType.TWOFA_SMS_DELETE, ChannelType.SMS);

    this.authService
      .endAuthorization(this.currentAuthRequest?.authRequestId ?? '', authorizationRequest)
      .pipe(
        catchError((resError: HttpErrorResponse) => {
          this.showErrorOnControl(this.verifyFormComponent?.codeForm.get('code') ?? null, resError.status, resError.error.errorKey);
          this.status = TwoFactorFlowStatus.FAILURE;
          return EMPTY;
        }),
        switchMap((authEndResponse: AuthorizationEndResponse) => {
          if (!authEndResponse.success) {
            this.showErrorOnControl(
              this.verifyFormComponent?.codeForm.get('code') ?? null,
              HttpStatusCode.Ok,
              authEndResponse.remainingAttempts > 0 ? 'authorization.codeError' : 'authorization.maximumRetryCount'
            );
            return EMPTY;
          }

          return this.twoSvSmsSrv.disable2fa(
            authEndResponse.authPermission,
            new ContactRequest(ContactType.MOBILE, this.mobileContact?.value ?? null)
          );
        }),
        catchError(() => {
          this.status = TwoFactorFlowStatus.FAILURE;
          return EMPTY;
        }),
        finalize(() => {
          this.verifyFormComponent?.hideSpinner();
        })
      )
      .subscribe(() => {
        this.status = TwoFactorFlowStatus.SUCCESS;
      });
  }

  /**
   * Invoke success function and wait for his execution,
   * than complete the Two factor app flow with final result step
   * @param authEndResponse Final authorization with authorization permission
   */
  callSuccessFunction(authEndResponse: AuthorizationEndResponse, canShowSpinner: boolean): void {
    this.successFunction?.call(this, authEndResponse)
      .pipe(
        finalize(() => {
          if (canShowSpinner) {
            this.verifyFormComponent?.hideSpinner();
          }
        })
      )
      .subscribe(
        () => {
          this.status = TwoFactorFlowStatus.SUCCESS;
        },
        () => {
          this.status = TwoFactorFlowStatus.FAILURE;
        }
      );
  }

  /**
   * Close current dialog and send the final authorization to parent component
   * @param authEndResponse Final authorization with authorization permission
   */
  closeTwoFactorAppFlow(authEndResponse?: AuthorizationEndResponse): void {
    if (this.status === TwoFactorFlowStatus.SUCCESS || authEndResponse) {
      this.activeModal.close(
        new WizardModalResult(WizardModalResultType.OTHER, authEndResponse, this.status === TwoFactorFlowStatus.GEN_CODE_SMS ? true : false)
      );
    } else {
      this.activeModal.dismiss(new WizardModalResult(WizardModalResultType.STRING, this.status));
    }
  }

  /**
   * Utility method for cleaning polling observer
   */
  unbindSubscription(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      delete this.pollingSubscription;
    }
  }

  /**
   * Unbind observable before component dismiss
   */
  ngOnDestroy(): void {
    this.unbindSubscription();
  }
}
